import React from 'react';
import {BlitzortungClient, LightningStrike} from '../utils/Blitzortung';
import {LIGHTNING_AGE_WINDOW_MS} from '../utils/Lightning';

export type LightningBounds = {
  latSw: number;
  lonSw: number;
  latNe: number;
  lonNe: number;
};

/** Strikes older than this are pruned (matches the visual fade window). */
export const LIGHTNING_MAX_AGE_MS = LIGHTNING_AGE_WINDOW_MS;
/** Buffer flush cadence — batches ws strikes into one render per interval. */
const FLUSH_INTERVAL_MS = 2000;
/** Newest strikes kept when the buffer overflows. */
const DEFAULT_MAX_STRIKES = 1000;
/** Viewport padding so panning reveals nearby already-received strikes. */
const BOUNDS_PADDING_FACTOR = 0.5;

const isInBounds = (
  strike: LightningStrike,
  bounds: LightningBounds | null | undefined,
) => {
  if (bounds == null) {
    return true;
  }
  const latPad = (bounds.latNe - bounds.latSw) * BOUNDS_PADDING_FACTOR;
  const lonPad = (bounds.lonNe - bounds.lonSw) * BOUNDS_PADDING_FACTOR;
  return (
    strike.lat >= bounds.latSw - latPad &&
    strike.lat <= bounds.latNe + latPad &&
    strike.lon >= bounds.lonSw - lonPad &&
    strike.lon <= bounds.lonNe + lonPad
  );
};

/**
 * Real-time lightning strikes from Blitzortung.org for the weather map.
 *
 * Maintains a rolling buffer of recent strikes (LIGHTNING_MAX_AGE_MS window),
 * filtered to the padded viewport bounds and capped at maxStrikes. The
 * websocket connection only lives while the screen is focused and the layer
 * is enabled; renders are throttled to one flush every FLUSH_INTERVAL_MS.
 */
const useLightningStrikes = (
  isFocused: boolean,
  enabled: boolean,
  bounds?: LightningBounds | null,
  maxStrikes: number = DEFAULT_MAX_STRIKES,
) => {
  const [strikes, setStrikes] = React.useState<LightningStrike[]>([]);
  const [connected, setConnected] = React.useState(false);
  const pendingRef = React.useRef<LightningStrike[]>([]);

  // Panning must not tear down the socket, so the viewport and the cap are
  // read when a strike arrives rather than tracked as effect dependencies --
  // which is what `useEffectEvent` is for. Both are called from callbacks the
  // effect creates (the ws handler and the flush interval), never from render.
  const currentBounds = React.useEffectEvent(() => bounds);
  const currentMaxStrikes = React.useEffectEvent(() => maxStrikes);

  const active = isFocused && enabled;

  React.useEffect(() => {
    if (!active) {
      return;
    }

    const client = new BlitzortungClient({
      onStrike: (strike) => {
        if (isInBounds(strike, currentBounds())) {
          pendingRef.current.push(strike);
        }
      },
      onConnectionChange: setConnected,
    });
    client.start();

    const flush = () => {
      const pending = pendingRef.current;
      pendingRef.current = [];
      const cutoff = Date.now() - LIGHTNING_MAX_AGE_MS;
      // Read before the updater, not inside it: React runs a state updater
      // during render, where calling an effect event throws.
      const bounds_ = currentBounds();
      const max = currentMaxStrikes();
      setStrikes((prev) => {
        const merged = pending.length > 0 ? prev.concat(pending) : prev;
        const filtered = merged.filter(
          (strike) => strike.time >= cutoff && isInBounds(strike, bounds_),
        );
        // Blitzortung re-delivers the same strike across its rotating ws hosts
        // and on reconnect; dedupe by id (latest delivery wins) so map markers
        // keep unique React keys and strike counts aren't double-inflated.
        const byId = new Map<string, LightningStrike>();
        for (const strike of filtered) {
          byId.set(strike.id, strike);
        }
        const deduped =
          byId.size === filtered.length ? filtered : Array.from(byId.values());
        const capped =
          deduped.length > max ? deduped.slice(deduped.length - max) : deduped;
        // Keep the previous array identity when nothing changed to avoid
        // re-rendering consumers every flush tick.
        if (pending.length === 0 && capped.length === prev.length) {
          return prev;
        }
        return capped;
      });
    };

    const timerId = setInterval(flush, FLUSH_INTERVAL_MS);

    return () => {
      clearInterval(timerId);
      client.stop();
      pendingRef.current = [];
      // `stop()` detaches the socket handlers without reporting a close, so
      // this is what tells the consumer the feed is down. In the cleanup
      // rather than in the effect body, where it was a guaranteed second
      // render pass on every mount (FE #180).
      setConnected(false);
    };
  }, [active]);

  return React.useMemo(() => ({strikes, connected}), [strikes, connected]);
};

export default useLightningStrikes;
